/**
 * The browser half of live text (server/textSessions.ts): one textarea taking
 * part in a field's session.
 *
 * The classic one-op-in-flight client. Every input is diffed against what the
 * box showed before (`shadow`) into an op; ops queue in `pending` and go to
 * the server one at a time, each tagged with the rev it was made against.
 * Someone else's op is transformed past everything still pending and spliced
 * into the box with the caret kept where it was. Remote carets are held in
 * the server's coordinates and moved through `pending` only for display.
 *
 * No Vue here: the store routes events in (`handle`) and hears about carets
 * through `onSels`; the `v-collab` directive owns the instance.
 */
import { applyOp, diffOp, isNoop, transform, transformIndex, type TextOp } from "@shared/textOt.js";
import type { ClientMsg, CollabField, ServerEvent, TextSel } from "@shared/types.js";

export type TextEvent = Extract<ServerEvent, { t: "textState" | "textOp" | "textAck" | "textSel" }>;

export interface RemoteSel {
  userId: string;
  sel: TextSel;
}

interface Pending {
  op: TextOp;
  cid: string;
}

/** Caret moves are sent at most this often. */
const SEL_MS = 100;
/** A server that keeps refusing the same edit (too long, say) wins after this many. */
const MAX_RESYNCS = 3;

let cidSeq = 0;
const newCid = (): string => `${Date.now().toString(36)}-${(cidSeq++).toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

export interface CollabOptions {
  send: (msg: ClientMsg) => void;
  /** Where the other people's carets are, in the box's own coordinates. */
  onSels: (sels: RemoteSel[]) => void;
  /** The box's text changed under the reader (someone else typed). */
  onRemote?: () => void;
  /** Keep what the box holds as an edit of ours, instead of showing `initial`:
   *  a draft just saved, still being typed in. */
  adopt?: boolean;
}

export class CollabText {
  /** The server's rev that `server` is at; null until the first `textState`. */
  private rev: number | null = null;
  /** The text at `rev`: everything acked, nothing of ours still pending. */
  private server: string;
  private pending: Pending[] = [];
  private inFlight = false;
  /** Joining or catching up: nothing is sent until the state has been taken in. */
  private holding = false;
  /** What the box shows: `server` with `pending` applied. */
  private shadow: string;
  /** Other people's carets, in `server`'s coordinates. */
  private sels = new Map<string, TextSel>();
  private resyncs = 0;
  private selTimer: ReturnType<typeof setTimeout> | null = null;
  private selDirty = false;
  /** Clicked or typed in since it was focused. The drawer focuses the title on
   *  its own when a task opens; a caret for merely looking would be noise. */
  private engaged = false;
  private closed = false;

  constructor(
    private readonly el: HTMLTextAreaElement | HTMLInputElement,
    readonly taskId: string,
    readonly field: CollabField,
    initial: string,
    private readonly opts: CollabOptions,
  ) {
    this.server = initial;
    this.shadow = initial;
    if (el.value !== initial) {
      if (opts.adopt) {
        const op = diffOp(initial, el.value);
        this.shadow = el.value;
        this.pending.push({ op, cid: newCid() });
      } else el.value = initial;
    }
    el.addEventListener("input", this.onInput);
    el.addEventListener("blur", this.onBlur);
    el.addEventListener("pointerdown", this.onEngage);
    el.addEventListener("keydown", this.onEngage);
    document.addEventListener("selectionchange", this.onSelection);
    this.join();
  }

  /** (Re)join, naming the rev we have so a reconnect can catch up op by op. */
  join(): void {
    // Whatever was in flight is accounted for by the state the join brings.
    this.inFlight = false;
    this.holding = true;
    this.opts.send({ t: "textJoin", taskId: this.taskId, field: this.field, ...(this.rev === null ? {} : { rev: this.rev }) });
  }

  destroy(): void {
    if (this.closed) return;
    this.closed = true;
    this.el.removeEventListener("input", this.onInput);
    this.el.removeEventListener("blur", this.onBlur);
    this.el.removeEventListener("pointerdown", this.onEngage);
    this.el.removeEventListener("keydown", this.onEngage);
    document.removeEventListener("selectionchange", this.onSelection);
    if (this.selTimer) clearTimeout(this.selTimer);
    this.opts.send({ t: "textLeave", taskId: this.taskId, field: this.field });
    this.opts.onSels([]);
  }

  /**
   * The task row's value, which the store changed. Once joined the session is
   * the box's source of truth and brings every change itself, so this only
   * matters before that (the socket down, say) — and then like `v-synced`:
   * taken unless the reader has typed something of their own.
   */
  serverValue(value: string): void {
    if (this.rev !== null || this.pending.length > 0 || value === this.shadow) return;
    this.splice(diffOp(this.shadow, value));
    this.server = value;
  }

  /** Whether everything typed here has reached the server. */
  get settled(): boolean {
    return this.pending.length === 0;
  }

  handle(e: TextEvent): void {
    if (this.closed) return;
    switch (e.t) {
      case "textState":
        return this.onState(e);
      case "textAck":
        return this.onAck(e.rev);
      case "textOp":
        return this.onRemoteOp(e.op, e.rev);
      case "textSel":
        if (e.sel) this.sels.set(e.userId, e.sel);
        else this.sels.delete(e.userId);
        return this.emitSels();
    }
  }

  // ── local edits ─────────────────────────────────────────────────────────
  private onInput = (): void => {
    this.engaged = true;
    const value = this.el.value;
    if (value === this.shadow) return;
    const op = diffOp(this.shadow, value, this.el.selectionEnd ?? undefined);
    this.shadow = value;
    if (!isNoop(op)) this.pending.push({ op, cid: newCid() });
    this.flush();
    this.emitSels();
    this.selChanged();
  };

  private flush(): void {
    if (this.inFlight || this.holding || this.rev === null || this.pending.length === 0) return;
    const next = this.pending[0]!;
    this.inFlight = true;
    this.opts.send({ t: "textOp", taskId: this.taskId, field: this.field, rev: this.rev, op: next.op, cid: next.cid });
  }

  private onAck(rev: number): void {
    const done = this.pending.shift();
    if (done) {
      this.server = applyOp(this.server, done.op);
      this.moveSels(done.op);
    }
    this.rev = rev;
    this.inFlight = false;
    this.resyncs = 0;
    this.flush();
    if (this.selDirty) this.sendSel();
  }

  // ── other people's edits ────────────────────────────────────────────────
  private onRemoteOp(op: TextOp, rev: number): void {
    this.server = applyOp(this.server, op);
    this.rev = rev;
    this.moveSels(op);
    // Past everything of ours the server has not seen, onto what the box shows.
    let incoming = op;
    for (const p of this.pending) [incoming, p.op] = transform(incoming, p.op);
    this.splice(incoming);
    this.emitSels();
  }

  /** Apply `op` to the box, keeping the reader's own selection where it was. */
  private splice(op: TextOp): void {
    const el = this.el;
    const focused = document.activeElement === el;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const dir = el.selectionDirection ?? "none";
    const scroll = el.scrollTop;
    this.shadow = applyOp(this.shadow, op);
    el.value = this.shadow;
    if (focused) el.setSelectionRange(transformIndex(start, op, false), transformIndex(end, op, false), dir);
    el.scrollTop = scroll;
    this.opts.onRemote?.();
  }

  private onState(e: Extract<TextEvent, { t: "textState" }>): void {
    this.holding = true;
    this.sels = new Map(e.sels.map((s) => [s.userId, s.sel]));
    if (e.ops && this.rev !== null) {
      // A rejoin: replay what we missed. Our own in-flight op, if it landed, is
      // among them under its cid — that one is an ack, the rest are others'.
      let rev = this.rev;
      for (const { op, cid } of e.ops) {
        rev++;
        if (cid !== null && this.pending[0]?.cid === cid) this.onAck(rev);
        else this.onRemoteOp(op, rev);
      }
      this.inFlight = false;
      this.holding = false;
      this.rev = e.rev;
      this.flush();
      this.emitSels();
      return;
    }
    // A first join, or a resync after the server refused an op or lost the
    // session. What we have not had acked is applied on top of what it holds.
    const base = this.server;
    this.rev = e.rev;
    this.server = e.text;
    this.inFlight = false;
    this.holding = false;
    if (this.pending.length === 0) {
      if (this.shadow !== e.text) this.replaceBox(e.text);
    } else if (++this.resyncs > MAX_RESYNCS) {
      this.pending = [];
      this.replaceBox(e.text);
    } else if (base !== e.text) {
      // Nothing to rebase onto: what this box shows wins.
      const op = diffOp(e.text, this.shadow);
      this.pending = isNoop(op) ? [] : [{ op, cid: newCid() }];
    }
    this.flush();
    this.emitSels();
  }

  private replaceBox(text: string): void {
    this.splice(diffOp(this.shadow, text));
  }

  // ── carets ──────────────────────────────────────────────────────────────
  private moveSels(op: TextOp): void {
    for (const [userId, [a, b]] of this.sels) this.sels.set(userId, [transformIndex(a, op), transformIndex(b, op)]);
  }

  private emitSels(): void {
    const out: RemoteSel[] = [];
    for (const [userId, sel] of this.sels) {
      let [a, b] = sel;
      for (const p of this.pending) {
        a = transformIndex(a, p.op);
        b = transformIndex(b, p.op);
      }
      const max = this.shadow.length;
      out.push({ userId, sel: [Math.min(a, max), Math.min(b, max)] });
    }
    this.opts.onSels(out);
  }

  private onEngage = (): void => {
    this.engaged = true;
    this.selChanged();
  };

  private onSelection = (): void => {
    if (document.activeElement === this.el) this.selChanged();
  };

  private onBlur = (): void => {
    this.engaged = false;
    this.selDirty = false;
    if (this.selTimer) clearTimeout(this.selTimer);
    this.selTimer = null;
    if (this.rev !== null) this.opts.send({ t: "textSel", taskId: this.taskId, field: this.field, rev: this.rev, sel: null });
  };

  private selChanged(): void {
    this.selDirty = true;
    if (this.selTimer) return;
    this.selTimer = setTimeout(() => {
      this.selTimer = null;
      if (this.selDirty) this.sendSel();
    }, SEL_MS);
  }

  /** Our caret, in the server's coordinates — so only once nothing of ours is pending. */
  private sendSel(): void {
    if (!this.engaged || this.rev === null || this.pending.length > 0 || document.activeElement !== this.el) return;
    this.selDirty = false;
    const sel: TextSel = this.el.selectionDirection === "backward"
      ? [this.el.selectionEnd ?? 0, this.el.selectionStart ?? 0]
      : [this.el.selectionStart ?? 0, this.el.selectionEnd ?? 0];
    this.opts.send({ t: "textSel", taskId: this.taskId, field: this.field, rev: this.rev, sel });
  }
}
